import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CUSTOMER_QUOTE,
  CustomerQuote,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  EMPTY_OBJECT,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  QuoteStatusEnum,
  Toggle,
  WSBlotterTableMaxRecordsReachedWarning,
  filterByColumnMainMenuItems,
  isGridApiReady,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useDisclosureWithContext,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type Column,
  type WebsocketRequest,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';

import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import type {
  GetMainMenuItemsParams,
  GridOptions,
  IsRowSelectable,
  RowDoubleClickedEvent,
  RowSelectionOptions,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { FixQuotePriceDialog } from '../../../components/FixQuotePriceDialog/FixQuotePriceDialog';
import { useFeatureFlag } from '../../../hooks';
import { useCustomerQuoteColumns } from './useCustomerQuotesColumns';
import {
  colIDToFilterBuilderKey,
  onlyServerFilterKeys,
  useCustomerQuotesFilter,
  type CustomerQuotesTableFilter,
} from './useCustomerQuotesFilter';
import { useCustomerQuotesMenu } from './useCustomerQuotesMenu';

interface CustomerQuotesTabData {
  showAllDecimals?: boolean;
}

export interface FilteredCustomerQuotesParams {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: (keyof CustomerQuote | Partial<Column>)[];
  defaultFilter?: CustomerQuotesTableFilter;
  initialIsOpen?: boolean;
  initialRequest?: Partial<WebsocketRequest>;
  portalId?: string;

  onCloneTab?: () => void;
  onRowDoubleClicked?: (params: RowDoubleClickedEvent<CustomerQuote>) => void;
  source?: 'monitoring' | 'dealer-monitoring' | 'manual-rfqs';

  tabData?: CustomerQuotesTabData;
  onUpdateTabData?: ({ showAllDecimals }: CustomerQuotesTabData) => void;
}

const isRowSelectable: IsRowSelectable<CustomerQuote> = row => row.data?.QuoteStatus === QuoteStatusEnum.PendingFix;

/** Row selection settings if the enableMakerMarketplaceETFRFQFlow flag is enabled. */
const SELECTION_MULTI_PARAMS_CHECKBOXES = {
  mode: 'multiRow',
  hideDisabledCheckboxes: true,
  checkboxes: true,
  headerCheckbox: true,
  enableClickSelection: false,
  isRowSelectable,
} as const satisfies RowSelectionOptions;

export function CustomerQuotesBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  initialRequest,
  onCloneTab,
  onRowDoubleClicked,
  source = 'dealer-monitoring',
  portalId,
  tabData,
  onUpdateTabData,
}: FilteredCustomerQuotesParams) {
  const { showAllDecimals } = tabData ?? EMPTY_OBJECT;
  const { getConfig } = useOrgConfiguration();
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const mixpanel = useMixpanel();
  const { enableMakerMarketplaceETFRFQFlow } = useFeatureFlag();

  const columns = useCustomerQuoteColumns({
    defaultColumns,
    source,
  });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const [selectedRows, setSelectedRows] = useState<CustomerQuote[]>([]);
  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<CustomerQuote>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const rows = api.getSelectedRows();
    setSelectedRows(rows.filter(node => node.QuoteStatus === QuoteStatusEnum.PendingFix));
  }, []);

  const filterResults = useCustomerQuotesFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const CustomerQuotesMenu = useCustomerQuotesMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const rowSelection = useMemo((): GridOptions['rowSelection'] => {
    const baseRowSelection = enableMakerMarketplaceETFRFQFlow
      ? SELECTION_MULTI_PARAMS_CHECKBOXES
      : DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS;
    return {
      ...baseRowSelection,
      enableClickSelection: false,
    };
  }, [enableMakerMarketplaceETFRFQFlow]);

  const columnsWithMenu = useMemo<Column[]>(
    () => [...columns, ...CustomerQuotesMenu.columns],
    [CustomerQuotesMenu.columns, columns]
  );

  const { showBlotterPauseButton } = useFeatureFlag();

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: CUSTOMER_QUOTE,
      sort_by: '-SubmitTime',
      ...initialRequest,
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, initialRequest, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: CustomerQuote.rowID,
    clientLocalFilter,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    getExtraMainMenuItems,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    settings: blotterTableSettings,
    contextMenuOptions: { getCustomContextMenuItems: CustomerQuotesMenu.getCustomContextMenuItems },
    gridOptions: {
      onSelectionChanged: handleRowSelectionChanged,
      onRowDoubleClicked,
      rowSelection,
    },
    exportOptions: {
      name: 'CustomerQuotes',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  const handleShowAllDecimalsChange = useCallback(
    (value: boolean) => {
      onUpdateTabData?.({ showAllDecimals: value });
    },
    [onUpdateTabData]
  );

  const fixQuotePriceDialog = useDisclosureWithContext<CustomerQuote[]>();

  const applyFixingEnabled =
    selectedRows.length === 0 ||
    selectedRows.every(
      row =>
        row.QuoteStatus === QuoteStatusEnum.PendingFix &&
        row.FixingDetails?.Index === selectedRows[0].FixingDetails?.Index
    );

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        portalId={portalId}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
            {/* Only show this toggle if the blotter parent supports storing the state */}
            {!showAllDecimalsInBlotters && onUpdateTabData && (
              <Toggle
                size={FormControlSizes.Small}
                checked={showAllDecimals ?? false}
                onChange={handleShowAllDecimalsChange}
                label="Show All Decimals"
              />
            )}
          </BlotterTableExtrasMenu>
        }
        prefix={
          enableMakerMarketplaceETFRFQFlow ? (
            <>
              <Button
                size={FormControlSizes.Small}
                variant={ButtonVariants.Primary}
                disabled={!applyFixingEnabled}
                onClick={() => fixQuotePriceDialog.open(selectedRows)}
                data-testid="apply-fixing-button"
              >
                Apply Fixing
              </Button>
            </>
          ) : undefined
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.SubmitTime} />
      <BlotterTable {...blotterTable} />
      {CustomerQuotesMenu.dialogs}
      {enableMakerMarketplaceETFRFQFlow && <FixQuotePriceDialog {...fixQuotePriceDialog} />}
    </>
  );
}
